/**
 * DC-1822 — the decision-record preview and download.
 *
 * "Preview decision record" opens the SAME server-rendered HTML the PDF is
 * printed from — never a second React facsimile — inside a `sandbox=""`
 * iframe populated via `srcDoc`. The document is fetched with the bearer
 * token through `authFetch` (an iframe URL navigation could not attach it,
 * and the global `X-Frame-Options: DENY` would refuse it anyway; srcDoc
 * content is inline, so the framing protection stays intact). The sandbox
 * has an empty allowlist: no scripts run, which is fine — the record's
 * entrance choreography is pure CSS and plays on load, and reduced-motion
 * users get the final state immediately from the document's own media query.
 *
 * "Download PDF" activates as soon as the preview response is ok — never
 * gated on the animation finishing. Refusals surface the server's own
 * sentence (409 DECISION_REQUIRED reads "Record a decision to issue this
 * one-pager"), so this surface cannot drift from the route's contract.
 */

import React, { useCallback, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { useApiClient } from '../../hooks/useApiClient';

type PreviewState =
  | { phase: 'loading' }
  | { phase: 'error'; message: string }
  | { phase: 'ready'; html: string };

async function refusalMessage(response: Response, fallback: string): Promise<string> {
  try {
    const body = (await response.json()) as { error?: { message?: string } };
    return body.error?.message ?? fallback;
  } catch {
    return fallback;
  }
}

export const DecisionRecordPreview: React.FC<{
  useCaseId: string;
  runId: string;
  decisionId: string;
  useCaseRef: string;
}> = ({ useCaseId, runId, decisionId, useCaseRef }) => {
  const { authFetch } = useApiClient();
  const [open, setOpen] = useState(false);
  const [state, setState] = useState<PreviewState>({ phase: 'loading' });
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  const recordUrl = `/api/use-cases/${useCaseId}/assessment-runs/${runId}/one-pager`;
  const decisionQuery = `decisionId=${encodeURIComponent(decisionId)}`;

  const load = useCallback(async () => {
    setState({ phase: 'loading' });
    try {
      const response = await authFetch(`${recordUrl}?format=html&${decisionQuery}`);
      if (!response.ok) {
        setState({
          phase: 'error',
          message: await refusalMessage(response, 'The decision record could not be prepared.'),
        });
        return;
      }
      setState({ phase: 'ready', html: await response.text() });
    } catch {
      setState({ phase: 'error', message: 'The decision record could not be prepared.' });
    }
  }, [authFetch, recordUrl, decisionQuery]);

  const download = useCallback(async () => {
    setDownloading(true);
    setDownloadError(null);
    try {
      const response = await authFetch(`${recordUrl}?format=pdf&${decisionQuery}`);
      if (!response.ok) {
        setDownloadError(await refusalMessage(response, 'The PDF could not be generated.'));
        return;
      }
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = `DATA_Compass_Decision_Record_${useCaseRef}_${runId.slice(0, 8)}.pdf`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
    } catch {
      setDownloadError('The PDF could not be generated.');
    } finally {
      setDownloading(false);
    }
  }, [authFetch, recordUrl, decisionQuery, useCaseRef, runId]);

  return (
    <Dialog.Root
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        setDownloadError(null);
        if (next) void load();
      }}
    >
      <Dialog.Trigger asChild>
        <button type="button" className="banking-btn banking-btn-secondary" data-testid="preview-decision-record">
          Preview decision record
        </button>
      </Dialog.Trigger>
      <Dialog.Portal>
        <Dialog.Overlay className="uc-record-overlay" />
        <Dialog.Content className="uc-record-dialog" aria-describedby={undefined}>
          <div className="uc-record-head">
            <Dialog.Title className="uc-record-title">Decision record — {useCaseRef}</Dialog.Title>
            <Dialog.Close asChild>
              <button type="button" className="banking-btn banking-btn-secondary" data-testid="close-decision-record">
                Close
              </button>
            </Dialog.Close>
          </div>

          {state.phase === 'loading' && (
            <p className="uc-record-status" role="status">
              Preparing the record…
            </p>
          )}
          {state.phase === 'error' && (
            <div className="uc-record-status" role="alert">
              <p>{state.message}</p>
              <button type="button" className="banking-btn banking-btn-secondary" onClick={() => void load()}>
                Retry
              </button>
            </div>
          )}
          {state.phase === 'ready' && (
            <iframe
              className="uc-record-frame"
              title="Decision record preview"
              sandbox=""
              srcDoc={state.html}
              data-testid="decision-record-frame"
            />
          )}

          <div className="uc-record-foot">
            <button
              type="button"
              className="banking-btn banking-btn-primary"
              data-testid="download-decision-record"
              disabled={state.phase !== 'ready' || downloading}
              onClick={() => void download()}
            >
              {downloading ? 'Generating…' : 'Download PDF'}
            </button>
            {downloadError && (
              <p className="uc-record-refusal" role="alert">
                {downloadError}
              </p>
            )}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export default DecisionRecordPreview;
